package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.TeamStrings
import com.milta.feature.team.domain.entity.Driver
import com.milta.feature.team.domain.usecase.GetEnterpriseDriversUseCase
import com.milta.feature.team.domain.usecase.GetMyTeamUseCase
import com.milta.feature.team.domain.usecase.LinkDriverUseCase
import com.milta.feature.team.domain.usecase.RemoveDriverUseCase
import com.milta.feature.team.presentation.WebTeamIntent
import com.milta.feature.team.presentation.WebTeamState
import com.milta.feature.team.presentation.WebTeamViewModel
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.koin.compose.koinInject

/**
 * Entry point wired from `Main.kt`'s "Équipe" nav destination (MIL-49) — owns the single
 * [WebTeamViewModel] driving the add-driver-by-code flow, the team table and the remove-driver
 * confirmation dialog. Layout mirrors the mockup's two-column split (table 75% / "Ajouter un
 * chauffeur" side panel 25%, `3fr 1fr` grid) — same shell pattern as
 * [WebVerifyAssignRouteScreen]'s two-column grid, ratio reversed since here the table is the
 * wider, primary column.
 */
@Composable
fun WebTeamContent() {
    val getMyTeam: GetMyTeamUseCase = koinInject()
    val getEnterpriseDrivers: GetEnterpriseDriversUseCase = koinInject()
    val linkDriver: LinkDriverUseCase = koinInject()
    val removeDriver: RemoveDriverUseCase = koinInject()
    val viewModel = remember { WebTeamViewModel(getMyTeam, getEnterpriseDrivers, linkDriver, removeDriver) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) { viewModel.onIntent(WebTeamIntent.LoadTeam) }

    val strings = LocalStrings.current.team
    val commonStrings = LocalStrings.current.common

    Div(
        attrs = {
            style {
                property("display", "grid")
                property("grid-template-columns", "3fr 1fr")
                property("gap", "18px")
                property("align-items", "start")
            }
        },
    ) {
        TeamTableCard(state = state, strings = strings, offlineMessage = commonStrings.errorOffline, retryLabel = commonStrings.retry, onIntent = viewModel::onIntent)
        AddDriverSection(state = state, strings = strings, offlineMessage = commonStrings.errorOffline, onIntent = viewModel::onIntent)
    }

    val pendingDriver = state.pendingRemovalDriverId?.let { id -> state.drivers.firstOrNull { it.id == id } }
    if (state.pendingRemovalDriverId != null) {
        RemoveDriverDialog(
            driverName = pendingDriver?.let { "${it.firstName} ${it.lastName}" }.orEmpty(),
            state = state,
            strings = strings,
            commonStrings = commonStrings,
            onIntent = viewModel::onIntent,
        )
    }
}

@Composable
private fun TeamTableCard(
    state: WebTeamState,
    strings: TeamStrings,
    offlineMessage: String,
    retryLabel: String,
    onIntent: (WebTeamIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebBorder")
                property("border-radius", "12px")
                property("overflow", "hidden")
            }
        },
    ) {
        Div(
            attrs = { style { padding(14.px, 18.px); property("border-bottom", "1px solid $WebBorder"); property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary) } },
        ) { Text(strings.enterpriseSectionLabel) }

        val loadError = state.error
        if (loadError != null && !state.isLoading) {
            // A failed load must never render the same "no drivers yet" hint as a genuine empty
            // team — see WebTeamState.error doc. Checked before the isEmpty() branch below.
            Div(attrs = { style { padding(18.px) } }) {
                InlineBanner(kind = InlineBannerKind.Error, text = teamLoadErrorMessage(loadError, strings, offlineMessage))
                Div(attrs = { style { property("margin-top", "12px"); property("text-align", "center") } }) {
                    Button(
                        attrs = {
                            onClick { onIntent(WebTeamIntent.LoadTeam) }
                            style {
                                property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                                property("background", WebSurface); property("color", WebTextPrimary)
                                property("font-size", "12.5px"); property("font-weight", "600")
                                property("padding", "7px 14px"); property("cursor", "pointer")
                            }
                        },
                    ) { Text(retryLabel) }
                }
            }
        } else if (state.drivers.isEmpty() && !state.isLoading) {
            Div(
                attrs = { style { padding(24.px, 18.px); property("font-size", "13px"); property("color", WebTextSecondary); property("text-align", "center") } },
            ) { Text(strings.enterpriseEmptyHint) }
        } else {
            Table(attrs = { style { width(100.percent); property("border-collapse", "collapse"); property("font-size", "13.5px") } }) {
                Thead {
                    Tr {
                        TeamHeaderCell(strings.columnHeaderDriver)
                        TeamHeaderCell(strings.driverDetailPhoneLabel)
                        TeamHeaderCell(strings.columnHeaderStatus)
                        TeamHeaderCell(strings.columnHeaderActions)
                    }
                }
                Tbody {
                    state.drivers.forEach { driver ->
                        DriverRow(driver = driver, statusActiveLabel = strings.statusActive, removeLabel = strings.removeDriverButton, onIntent = onIntent)
                    }
                }
            }
        }
    }
}

@Composable
private fun TeamHeaderCell(label: String) {
    Th(
        attrs = { style { property("text-align", "left"); property("font-size", "10.5px"); property("font-weight", "700"); property("letter-spacing", "0.05em"); property("text-transform", "uppercase"); property("color", WebTextSecondary); padding(10.px, 18.px); property("background", WebSurfaceDim); property("border-bottom", "1px solid $WebBorder") } },
    ) { Text(label) }
}

@Composable
private fun DriverRow(driver: Driver, statusActiveLabel: String, removeLabel: String, onIntent: (WebTeamIntent) -> Unit) {
    Tr {
        Td(attrs = { style { padding(10.px, 18.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px") } }) {
                DriverAvatar(name = "${driver.firstName} ${driver.lastName}")
                Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) {
                    Text("${driver.firstName} ${driver.lastName}")
                }
            }
        }
        Td(attrs = { style { padding(10.px, 18.px); property("color", WebTextSecondary); property("font-variant-numeric", "tabular-nums"); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
            Text(driver.phoneNumber)
        }
        Td(attrs = { style { padding(10.px, 18.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
            DriverStatusBadge(label = statusActiveLabel)
        }
        Td(attrs = { style { padding(10.px, 18.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
            Button(
                attrs = {
                    onClick { onIntent(WebTeamIntent.RequestRemoval(driver.id)) }
                    style {
                        property("border", "1px solid $WebError"); property("border-radius", "8px")
                        property("background", WebSurface); property("color", WebError)
                        property("font-size", "12.5px"); property("font-weight", "600")
                        property("padding", "7px 12px"); property("cursor", "pointer")
                    }
                },
            ) { Text(removeLabel) }
        }
    }
}

/** Mirrors [com.milta.feature.team.presentation.EnterpriseTeamScreen]'s `DriverRow` status pill
 * (green dot + "Actif" label) — hardcoded like mobile, since [Driver] carries no status field. */
@Composable
private fun DriverStatusBadge(label: String) {
    Div(
        attrs = {
            style {
                property("display", "inline-flex"); property("align-items", "center"); property("gap", "5px")
                property("background", "rgba(46,158,107,0.14)")
                property("border-radius", "999px")
                property("padding", "3px 10px")
            }
        },
    ) {
        Div(attrs = { style { width(6.px); height(6.px); property("border-radius", "50%"); property("background", "#2E9E6B") } })
        Div(attrs = { style { property("font-size", "11.5px"); property("font-weight", "700"); property("color", "#2E9E6B") } }) { Text(label) }
    }
}

@Composable
private fun DriverAvatar(name: String) {
    val label = initials(name)
    Div(
        attrs = {
            style {
                width(32.px); height(32.px)
                property("border-radius", "50%")
                property("background", avatarColorFor(label))
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("flex-shrink", "0")
                property("color", "#FFFFFF"); property("font-weight", "700"); property("font-size", "12.5px")
            }
        },
    ) { Text(label) }
}

@Composable
private fun AddDriverSection(
    state: WebTeamState,
    strings: TeamStrings,
    offlineMessage: String,
    onIntent: (WebTeamIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebBorder")
                property("border-radius", "12px")
                padding(18.px)
            }
        },
    ) {
        Div(
            attrs = { style { property("font-size", "15px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "12px") } },
        ) { Text(strings.addDriverSectionTitle) }

        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "10px") } }) {
            Input(
                type = InputType.Text,
                attrs = {
                    value(state.codeInput)
                    attr("placeholder", strings.addDriverCodePlaceholder)
                    onInput { event -> onIntent(WebTeamIntent.UpdateCodeInput(event.value)) }
                    style {
                        width(100.percent); property("box-sizing", "border-box")
                        property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                        property("padding", "11px 14px"); property("font-size", "13.5px")
                    }
                },
            )
            val disabled = state.isSubmittingCode || state.codeInput.isBlank()
            Button(
                attrs = {
                    if (!disabled) onClick { onIntent(WebTeamIntent.SubmitCode) }
                    style {
                        gradientButtonStyle(this)
                        width(100.percent)
                        if (disabled) {
                            property("opacity", "0.5")
                            property("cursor", "not-allowed")
                            property("box-shadow", "none")
                        }
                    }
                },
            ) { Text(strings.addDriverButton) }
        }

        val addDriverError = state.addDriverError
        if (addDriverError != null) {
            Div(attrs = { style { property("margin-top", "12px") } }) {
                InlineBanner(kind = InlineBannerKind.Error, text = addDriverErrorMessage(addDriverError, strings, offlineMessage))
            }
        }
    }
}

@Composable
private fun RemoveDriverDialog(
    driverName: String,
    state: WebTeamState,
    strings: TeamStrings,
    commonStrings: com.milta.core.localization.CommonStrings,
    onIntent: (WebTeamIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
                property("background", "rgba(30,58,74,0.45)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("z-index", "1000")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("background", WebSurface)
                    property("border-radius", "14px")
                    width(360.px)
                    property("box-sizing", "border-box")
                    property("box-shadow", "0 12px 32px rgba(30,58,74,0.25)")
                    padding(22.px)
                }
            },
        ) {
            Div(attrs = { style { property("font-size", "16px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "10px") } }) {
                Text(strings.removeDriverDialogTitle)
            }
            Div(attrs = { style { property("font-size", "13.5px"); property("color", WebTextSecondary); property("line-height", "1.5") } }) {
                Text("${strings.removeDriverDialogMessagePrefix} $driverName ${strings.removeDriverDialogMessageSuffix}")
            }

            val removalError = state.removalError
            if (removalError != null) {
                Div(attrs = { style { property("margin-top", "12px"); property("font-size", "12.5px"); property("color", WebErrorInk) } }) {
                    Text(removalErrorMessage(removalError, strings, commonStrings.errorOffline))
                }
            }

            Div(attrs = { style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "10px"); property("margin-top", "20px") } }) {
                Button(
                    attrs = {
                        onClick { onIntent(WebTeamIntent.CancelRemoval) }
                        style {
                            property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                            property("background", WebSurface); property("color", WebTextPrimary)
                            property("font-size", "13px"); property("font-weight", "600")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                        }
                    },
                ) { Text(commonStrings.cancel) }

                Button(
                    attrs = {
                        if (!state.isRemoving) onClick { onIntent(WebTeamIntent.ConfirmRemoval) }
                        style {
                            property("border", "none"); property("border-radius", "8px")
                            property("background", WebError); property("color", "#FFFFFF")
                            property("font-size", "13px"); property("font-weight", "700")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                            if (state.isRemoving) {
                                property("opacity", "0.6"); property("cursor", "not-allowed")
                            }
                        }
                    },
                ) { Text(strings.removeDriverButton) }
            }
        }
    }
}

/** Maps [WebTeamState.error] ("server_error" | "offline") — same shape as [addDriverErrorMessage]/
 * [removalErrorMessage] below, reusing [TeamStrings.errorGeneric] for the generic case. */
private fun teamLoadErrorMessage(code: String, strings: TeamStrings, offlineMessage: String): String = when (code) {
    "offline" -> offlineMessage
    else -> strings.errorGeneric
}

/** Mirrors [com.milta.feature.team.presentation.EnterpriseTeamScreen]'s `addError` mapping exactly
 * — "invalid_code" is the web equivalent of mobile's "validation_code" (see [WebTeamViewModel]). */
private fun addDriverErrorMessage(code: String, strings: TeamStrings, offlineMessage: String): String = when (code) {
    "invalid_code" -> strings.addDriverErrorInvalidCode
    "not_found" -> strings.addDriverErrorNotFound
    "already_member" -> strings.addDriverErrorAlreadyMember
    "linked_account_is_enterprise" -> strings.addDriverErrorLinkedAccountIsEnterprise
    "driver_limit_reached" -> strings.addDriverErrorLimitReached
    "enterprise_subscription_inactive" -> strings.addDriverErrorEnterpriseSubscriptionInactive
    "offline" -> offlineMessage
    else -> strings.errorGeneric
}

/** Mirrors [com.milta.feature.team.presentation.DriverDetailScreen]'s `state.error` mapping exactly. */
private fun removalErrorMessage(code: String, strings: TeamStrings, offlineMessage: String): String = when (code) {
    "on_active_delivery" -> strings.removeDriverErrorOnActiveDelivery
    "last_owner" -> strings.removeDriverErrorLastOwner
    "not_found" -> strings.removeDriverErrorNotFound
    "offline" -> offlineMessage
    else -> strings.errorGeneric
}
